// frontend/src/pages/Cart.jsx - COMPLETELY CLEANED
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import CurrencyDisplay from "../components/UI/CurrencyDisplay";
import Button from "../components/UI/Button";
import Loader from "../components/UI/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash,
  faShoppingCart,
  faTractor,
  faArrowRight,
  faMinus,
  faPlus,
  faCalendarCheck,
  faBox,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

const Cart = () => {
  const {
    cart,
    loading,
    updateItem,
    removeItem,
    emptyCart,
    getCartSubtotal,
    getCartItems,
  } = useCart();
  const { user } = useAuth();
  const { currency } = useCurrency();

  const userType = user?.userType || "customer";
  const isFarmer = userType === "farmer";

  const cartItems = getCartItems ? getCartItems() : cart?.items || [];

  const subtotalUSD = getCartSubtotal
    ? getCartSubtotal()
    : cartItems.reduce((sum, item) => {
        const price =
          item.finalPrice?.usd ||
          item.price?.usd ||
          item.product?.basePrice?.usd ||
          0;
        return sum + price * (item.quantity || 1);
      }, 0);

  const totalQuantity = cartItems.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0,
  );
  const isBulkCart = isFarmer && totalQuantity >= 100;
  const shippingUSD = isBulkCart || subtotalUSD >= 100 ? 0 : 10;
  const totalUSD = subtotalUSD + shippingUSD;

  if (loading) return <Loader />;

  if (!cartItems || cartItems.length === 0) {
    return (
      <motion.div
        className="cart-empty-page"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="empty-icon">🛒</div>
        <h2>Your Cart is Empty</h2>
        <p>
          Start adding products from our collection of premium livestock
          nutrition products sourced from Malaysia.
        </p>
        <div className="empty-actions">
          <Button href="/products" variant="primary" size="large">
            Continue Shopping
          </Button>
          <Button href="/preorder" variant="secondary" size="large">
            <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order GrowPro
          </Button>
          <a
            href="https://wa.me/23231030808"
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-empty"
          >
            <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Order
          </a>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="cart-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="container cart-container">
        <div className="cart-header">
          <h1>
            <FontAwesomeIcon icon={faShoppingCart} className="header-icon" />
            Shopping Cart
            <span className="cart-count">{cartItems.length} items</span>
          </h1>
          <div className="cart-header-actions">
            <Button href="/preorder" variant="secondary" size="small">
              <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
            </Button>
            <button onClick={emptyCart} className="clear-cart-btn">
              <FontAwesomeIcon icon={faTrash} /> Clear Cart
            </button>
          </div>
        </div>

        {isBulkCart && (
          <motion.div
            className="bulk-notice"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FontAwesomeIcon icon={faTractor} />
            <span>
              <strong>Bulk Order Detected!</strong> Your cart qualifies for bulk
              pricing. Proceed to checkout for special farmer rates.
            </span>
          </motion.div>
        )}

        <div className="cart-layout">
          <div className="cart-items">
            {cartItems.map((item, index) => {
              const itemPrice =
                item.finalPrice?.usd ||
                item.price?.usd ||
                item.product?.basePrice?.usd ||
                0;
              const itemTotal = itemPrice * (item.quantity || 1);
              const isGrowPro =
                item.slug === "kenali-growpro" ||
                item.productSlug === "kenali-growpro";
              const itemId = item._id || item.id;

              return (
                <motion.div
                  key={itemId || index}
                  className={`cart-item ${isGrowPro ? "growpro" : ""}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  exit={{ opacity: 0, x: -20 }}
                  layout
                >
                  <div className="item-image">
                    {item.image || item.product?.images?.[0] ? (
                      <img
                        src={item.image || item.product?.images[0]}
                        alt={item.name || item.product?.name || "Product"}
                      />
                    ) : (
                      <div className="item-image-placeholder">📦</div>
                    )}
                    {(isGrowPro || item.isPreOrder) && (
                      <span className="pre-order-badge">
                        <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order
                      </span>
                    )}
                  </div>

                  <div className="item-details">
                    <Link
                      to={`/products/${item.productSlug || item.product?.slug}`}
                      className="item-name"
                    >
                      {item.name || item.product?.name || "Product"}
                    </Link>
                    {(isGrowPro || item.isPreOrder) && (
                      <div className="pre-order-available">
                        <FontAwesomeIcon icon={faCalendarCheck} /> Pre-order
                        available
                      </div>
                    )}
                    <div className="item-price">
                      {item.quantity || 1} ×{" "}
                      <CurrencyDisplay amountUSD={itemPrice} compact />
                    </div>
                  </div>

                  <div className="item-total">
                    <CurrencyDisplay amountUSD={itemTotal} />
                  </div>

                  <div className="item-quantity">
                    <button
                      className="qty-btn"
                      onClick={() => {
                        const newQty = (item.quantity || 1) - 1;
                        if (newQty > 0) {
                          updateItem(itemId, newQty);
                        }
                      }}
                      aria-label="Decrease quantity"
                      disabled={(item.quantity || 1) <= 1}
                    >
                      <FontAwesomeIcon icon={faMinus} />
                    </button>
                    <input
                      type="number"
                      min={isFarmer ? 25 : 1}
                      value={item.quantity || 1}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (val > 0) updateItem(itemId, val);
                      }}
                      className="qty-input"
                      aria-label="Quantity"
                    />
                    <button
                      className="qty-btn"
                      onClick={() =>
                        updateItem(itemId, (item.quantity || 1) + 1)
                      }
                      aria-label="Increase quantity"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>

                  <button
                    className="item-remove"
                    onClick={() => removeItem(itemId)}
                    aria-label="Remove item"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </motion.div>
              );
            })}
          </div>

          <div className="cart-summary">
            <h3>Order Summary</h3>

            <div className="summary-row">
              <span className="label">Subtotal:</span>
              <span className="value">
                <CurrencyDisplay amountUSD={subtotalUSD} />
              </span>
            </div>

            <div className="summary-row">
              <span className="label">Shipping:</span>
              <span className="value">
                {shippingUSD === 0 ? (
                  <span className="free-shipping">Free</span>
                ) : (
                  <CurrencyDisplay amountUSD={shippingUSD} compact />
                )}
              </span>
            </div>

            <hr className="summary-divider" />

            <div className="summary-row total">
              <span>Total:</span>
              <span className="value">
                <CurrencyDisplay amountUSD={totalUSD} />
              </span>
            </div>

            {isFarmer && totalQuantity > 0 && totalQuantity < 100 && (
              <div className="bulk-notice-small">
                ⚠️ Add <strong>{100 - totalQuantity}kg</strong> more to qualify
                for farmer bulk pricing
              </div>
            )}

            <div className="summary-actions">
              <Button href="/checkout" variant="primary" fullWidth size="large">
                Proceed to Checkout <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button
                href="/preorder"
                variant="secondary"
                fullWidth
                size="medium"
              >
                <FontAwesomeIcon icon={faCalendarCheck} /> Pre-Order GrowPro
              </Button>
              <Button
                href="/products"
                variant="outline"
                fullWidth
                size="medium"
              >
                Continue Shopping
              </Button>
            </div>

            <div className="summary-footer">
              <small>
                {isFarmer
                  ? "Bulk orders get priority processing"
                  : "Free shipping on orders over $100"}
              </small>
              <div className="summary-supplier">
                <FontAwesomeIcon icon={faBox} />
                <span>Sourced from Mylasha, Malaysia</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Cart;
